import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { AUTH_HEADER_NAME, apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";

interface ForgotPasswordState {
  step: 1 | 2 | 3;
  mobile: string;
  otpAuthToken: string;
  status: "idle" | "loading" | "success" | "error";
  error: string;
}

const initialState: ForgotPasswordState = {
  step: 1,
  mobile: "",
  otpAuthToken: "",
  status: "idle",
  error: "",
};

export const generateOtp = createAsyncThunk<void, string, {}>(
  "forgotPassword/generateOtp",
  async (mobile, thunkApi) => {
    return API.post(apis.generateOtp, { mobile })
      .then(() => undefined)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Failed to send OTP. Please try again.";
        return thunkApi.rejectWithValue({ message: errorMessage });
      });
  }
);

interface VerifyOtpParams {
  mobile: string;
  otp: string;
}

interface VerifyOtpResponse {
  authToken: string;
}

export const verifyOtp = createAsyncThunk<VerifyOtpResponse, VerifyOtpParams, {}>(
  "forgotPassword/verifyOtp",
  async ({ mobile, otp }, thunkApi) => {
    return API.post(apis.verifyOtp, { mobile, otp, deviceId: "wms-web-portal" })
      .then((response) => response.data)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Invalid OTP. Please try again.";
        return thunkApi.rejectWithValue({ message: errorMessage });
      });
  }
);

interface ResetPasswordParams {
  newPassword: string;
  authToken: string;
}

export const resetPassword = createAsyncThunk<void, ResetPasswordParams, {}>(
  "forgotPassword/resetPassword",
  async ({ newPassword, authToken }, thunkApi) => {
    return API.post(
      apis.resetPassword,
      { newPassword },
      { headers: { [AUTH_HEADER_NAME]: authToken } }
    )
      .then(() => undefined)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Failed to reset password. Please try again.";
        return thunkApi.rejectWithValue({ message: errorMessage });
      });
  }
);

const forgotPasswordSlice = createSlice({
  name: "forgotPassword",
  initialState,
  reducers: {
    setMobile: (state, action) => {
      state.mobile = action.payload;
    },
    resetFlow: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(generateOtp.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(generateOtp.fulfilled, (state) => {
        state.status = "success";
        state.step = 2;
      })
      .addCase(generateOtp.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        state.error = error?.message || "Something went wrong!";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = "success";
        state.otpAuthToken = action.payload.authToken;
        state.step = 3;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        state.error = error?.message || "Something went wrong!";
      })
      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        state.error = error?.message || "Something went wrong!";
      });
  },
});

export const { setMobile, resetFlow } = forgotPasswordSlice.actions;
export default forgotPasswordSlice.reducer;
